// App-wide error boundary. React unmounts the whole tree when a render throws;
// without a boundary the customer is left on a blank page (which reads as "the
// app crashed / reloaded"). This catches those errors and shows a localized,
// friendly reload prompt instead — a last-resort safety net around the live
// capture UI, not a substitute for fixing the underlying throw.

import { Component, type ErrorInfo, type ReactNode } from "react";

import { useI18n } from "../lib/i18n";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

// The fallback lives in its own function component so it can use the i18n hook
// (a class component can't). It renders inside <I18nProvider>, so `t` resolves.
export function CrashFallback(): React.ReactElement {
  const { t } = useI18n();
  return (
    <div
      className="mx-auto max-w-md px-4 py-16 text-center"
      data-testid="crash-fallback"
    >
      <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-5">
        <h2 className="font-semibold text-rose-200">{t("crash.title")}</h2>
        <p className="mt-2 text-sm text-rose-100/90">{t("crash.body")}</p>
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="mt-4 w-full rounded-full bg-white py-3.5 text-base font-semibold text-slate-900 shadow-lg transition active:bg-slate-100"
      >
        {t("crash.reload")}
      </button>
    </div>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to the console for field debugging; there's no remote logging.
    console.error("Uncaught render error:", error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) return <CrashFallback />;
    return this.props.children;
  }
}
